import {
  createSystem,
  defaultConfig,
  defineConfig,
  defineRecipe,
  defineSemanticTokens,
  defineSlotRecipe,
  defineTokens,
} from '@chakra-ui/react';
import { fontDefault, fontMonospaced } from '@m/styled/fonts';

import { typographyRecipe, typographyMixins } from './typography';

export const buttonRecipe = defineRecipe({
  base: {
    padding: '16px',
    fontSize: '16px',
    fontWeight: '600',
  },
  variants: {
    size: {
      sm: {
        padding: '10px',
        fontSize: '14px',
        fontWeight: '500',
      },
      md: {
        fontWeight: '500',
      },
      lg: {
        minWidth: {
          // Desktop
          lg: '96px',
        },
      },
    },
    variant: {
      primary: {
        bg: 'fill.button.primary.enabled',
        color: 'text.white',
        _hover: {
          bg: 'fill.button.primary.hover',
        },
        _active: {
          bg: 'fill.button.primary.active',
        },
        _disabled: {
          bg: 'fill.button.primary.disabled',
          color: 'text.disabled',
        },
      },
      secondary: {
        bg: 'fill.button.secondary.enabled',
        color: 'text.primary',
        _hover: {
          bg: 'fill.button.secondary.hover',
        },
        _active: {
          bg: 'fill.button.secondary.active',
        },
        _disabled: {
          bg: 'fill.button.secondary.disabled',
          color: 'text.disabled',
        },
      },
      tertiary: {
        bg: 'fill.button.tertiary.enabled',
        borderColor: 'stroke.border.tertiary.enabled',
        color: 'text.primary',
        _hover: {
          bg: 'fill.button.tertiary.hover',
          borderColor: 'stroke.button.tertiary.hover',
        },
        _active: {
          bg: 'fill.button.tertiary.active',
          borderColor: 'stroke.button.tertiary.active',
        },
        _disabled: {
          bg: 'fill.button.tertiary.disabled',
          color: 'text.disabled',
          borderColor: 'stroke.button.tertiary.disabled',
        },
      },
      brand: {
        bg: 'fill.button.pay.enabled',
        color: 'text.white',
        _hover: {
          bg: 'fill.button.pay.hover',
        },
        _active: {
          bg: 'fill.button.pay.active',
        },
        _disabled: {
          bg: 'fill.button.pay.disabled',
          color: 'text.disabled',
        },
      },
      link: {
        ...typographyMixins.Link,
      },
    },
    destructive: {
      true: {},
    },
  },
  compoundVariants: [
    {
      destructive: true,
      variant: 'primary',
      css: {
        bg: 'fill.button.destructive-primary.enabled',
        _hover: {
          bg: 'fill.button.destructive-primary.hover',
        },
        _active: {
          bg: 'fill.button.destructive-primary.active',
        },
        _disabled: {
          bg: 'fill.button.destructive-primary.disabled',
          color: 'text.disabled',
        },
      },
    },
    {
      destructive: true,
      variant: 'secondary',
      css: {
        bg: 'fill.button.destructive-secondary.enabled',
        borderColor: 'stroke.button.destructive-secondary.enabled',
        color: 'text.destructive-secondary.enabled',
        _hover: {
          bg: 'fill.button.destructive-secondary.hover',
          borderColor: 'stroke.button.destructive-secondary.hover',
          color: 'text.destructive-secondary.hover',
        },
        _active: {
          bg: 'fill.button.destructive-secondary.active',
          borderColor: 'stroke.button.destructive-secondary.active',
          color: 'text.destructive-secondary.active',
        },
        _disabled: {
          bg: 'fill.button.destructive-secondary.disabled',
          borderColor: 'stroke.button.destructive-secondary.disabled',
          color: 'text.disabled',
        },
      },
    },
  ],
});

export const badgeRecipe = defineRecipe({
  base: {
    ...typographyMixins.ExtraSmallBody,
    borderRadius: 'full',
    gap: 2,
  },
  variants: {
    variant: {
      brand: {
        bg: 'brand.100',
        color: 'brand.700',
      },
      neutral: {
        bg: 'gray.200',
        color: 'gray.800',
      },
      informational: {
        bg: 'blue.100',
        color: 'blue.700',
      },
      positive: {
        bg: 'green.100',
        color: 'green.700',
      },
      warning: {
        bg: 'yellow.100',
        color: 'yellow.700',
      },
      negative: {
        bg: 'red.100',
        color: 'red.700',
      },
      urgent: {
        bg: 'red.700',
        color: 'gray.000',
      },
    },
    size: {
      sm: {
        px: 2,
        py: 1,
      },
    },
  },
});

export const inputRecipe = defineRecipe({
  base: {
    borderColor: 'stroke.border.default',
    _focusVisible: {
      focusRingColor: 'stroke.border.focused',
    },
  },
  variants: {
    readOnly: {
      true: {
        bg: 'fill.input.read-only',
        color: 'text.read-only',
      },
    },
    disabled: {
      true: {
        borderColor: 'stroke.border.disabled',
        color: 'text.disabled',
      },
    },
    variant: {
      outline: {
        borderColor: 'stroke.border.default',
        focusRingColor: 'stroke.border.focused',
      },
    },
  },
});

export const linkRecipe = defineRecipe({
  variants: {
    variant: {
      underline: {
        textDecorationColor: 'inherit',
      },
    },
  },
});

export const separatorRecipe = defineRecipe({
  base: {
    borderColor: 'stroke.divider.default',
  },
});

export const spinnerRecipe = defineRecipe({
  base: {
    color: 'fill.icon.descriptive',
    borderBottomColor: 'fill.icon.active',
    borderInlineStartColor: 'fill.icon.active',
  },
});

export const cardRecipe = defineRecipe({
  base: {
    bg: 'fill.background.default',
    borderRadius: 'md',
  },
  variants: {
    variant: {
      elevated: {
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05), 0 1px 4px rgba(0, 0, 0, 0.08)',
        border: '0',
        borderWidth: '0',
      },
      outline: {
        border: '1px solid',
        borderColor: 'stroke.border.default',
      },
    },
  },
});

export const fieldRecipe = defineSlotRecipe({
  slots: ['root', 'label', 'helperText', 'errorText', 'optionalIndicator'],
  base: {
    root: {
      gap: 1,
    },
    label: {
      ...typographyMixins.SmallBody,
      fontWeight: '500',
    },
    helperText: {
      ...typographyMixins.ExtraSmallBody,
      mt: 0.5,
    },
    errorText: {
      ...typographyMixins.ExtraSmallBody,
      mt: 0.5,
    },
    optionalIndicator: {
      fontWeight: '400',
    },
  },
});

export const tagRecipe = defineSlotRecipe({
  slots: ['root', 'label'],
  base: {
    root: {
      borderRadius: 'sm',
    },
  },
  variants: {
    variant: {
      subtle: {
        root: {
          bg: 'fill.background.light-gray',
          color: 'text.primary',
        },
      },
      solid: {
        root: {
          bg: 'fill.background.light-gray',
          color: 'text.primary',
        },
      },
      surface: {
        root: {
          bg: 'fill.background.light-gray',
          color: 'text.primary',
        },
      },
    },
    size: {
      xl: {
        root: {
          px: 3,
          py: 2.5,
        },
        label: {
          ...typographyMixins.SmallBody,
          fontWeight: '500',
        },
      },
    },
  },
  defaultVariants: {
    variant: 'solid',
    size: 'xl',
  },
});

export const accordionSlotRecipe = defineSlotRecipe({
  slots: ['itemIndicator'],
  base: {
    itemIndicator: {
      mr: 4,
      color: 'text.primary',
    },
  },
});

export const dialogSlotRecipe = defineSlotRecipe({
  slots: [
    'backdrop',
    'body',
    'closeTrigger',
    'content',
    'description',
    'footer',
    'header',
    'positioner',
    'title',
    'trigger',
  ],
  base: {
    header: {
      borderBottom: '1px solid',
      borderColor: 'stroke.divider.default',
      p: 6,
    },
    footer: {
      borderTop: '1px solid',
      borderColor: 'stroke.divider.default',
      p: 6,
    },
    body: {
      p: 6,
    },
  },
});

export const statusSlotRecipe = defineSlotRecipe({
  slots: ['root', 'indicator'],
  base: {
    indicator: {
      borderWidth: '.25em',
      width: '1em',
      height: '1em',
    },
  },
  variants: {
    variant: {
      positive: {
        indicator: { bg: 'green.400', borderColor: 'green.100' },
      },
      negative: {
        indicator: { bg: 'red.400', borderColor: 'red.100' },
      },
      neutral: {
        indicator: { bg: 'gray.600', borderColor: 'gray.300' },
      },
      warning: {
        indicator: { bg: 'yellow.500', borderColor: 'yellow.100' },
      },
      info: {
        indicator: { bg: 'blue.400', borderColor: 'blue.100' },
      },
    },
  },
});

export const switchSlotRecipe = defineSlotRecipe({
  slots: ['root', 'label', 'control', 'thumb', 'indicator', 'helperText', 'errorText'],
  base: {
    root: {
      gap: 4,
    },
    label: {
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
    },
    helperText: {
      ...typographyMixins.SmallBody,
      color: 'text.secondary',
    },
    errorText: {
      ...typographyMixins.ExtraSmallBody,
      alignItems: 'center',
      color: 'text.error',
      display: 'flex',
      gap: 1,
    },
  },
  variants: {
    variant: {
      solid: {
        control: {
          bg: 'gray.400',
          _checked: {
            bg: 'brand.400',
          },
        },
        thumb: {
          bg: 'fill.background.default',
          _checked: {
            bg: 'brand.100',
          },
        },
      },
    },

    size: {
      xs: {
        root: {
          '--switch-width': 'sizes.6',
          '--switch-height': 'sizes.3',
        },
      },
      sm: {
        root: {
          '--switch-width': 'sizes.7',
          '--switch-height': 'sizes.4',
        },
      },
      md: {
        root: {
          '--switch-width': 'sizes.9',
          '--switch-height': 'sizes.5',
        },
      },
      lg: {
        root: {
          '--switch-width': 'sizes.12',
          '--switch-height': 'sizes.7',
        },
      },
    },
  },
});

export const toastSlotRecipe = defineSlotRecipe({
  slots: ['root', 'title', 'description', 'indicator', 'closeTrigger', 'actionTrigger'],
  base: {
    root: {
      alignItems: 'center',
      boxShadow: 'md',
      gap: 2,
      px: 4,
      py: 2,
      '&[data-type=warning]': {
        bg: 'yellow.300',
        color: 'text.primary',
      },
      '&[data-type=success]': {
        bg: 'green.500',
        color: 'text.white',
      },
      '&[data-type=neutral]': {
        bg: 'gray.900',
        color: 'text.white',
      },
      '&[data-type=error]': {
        bg: 'red.200',
        color: 'text.primary',
      },
    },
    indicator: {
      boxSize: 6,
    },
  },
});

export const tooltipSlotRecipe = defineSlotRecipe({
  slots: ['content', 'arrow', 'arrowTip'],
  base: {
    content: {
      ...typographyMixins.SmallBody,
      fontWeight: '500',
      maxWidth: '240px',
      whiteSpace: 'normal',
    },
  },
});

export const stepperSlotRecipe = defineSlotRecipe({
  slots: ['root', 'step'],
  base: {
    root: {
      width: '100%',
      gap: 1,
    },
    step: {
      flexGrow: 1,
      height: 1,
      transition: 'background .2s',
    },
  },
  variants: {
    variant: {
      UserControlled: {
        step: {
          '&[data-state=completed]': {
            bg: 'brand.400',
          },
          '&[data-state=active]': {
            bg: 'brand.600',
          },
          '&[data-state=inactive]': {
            bg: 'gray.400',
          },
        },
      },
      SystemControlled: {
        root: {
          gap: '5px',
        },
        step: {
          height: 2,
          borderRadius: 2,
          position: 'relative',
          overflow: 'hidden',
          transition: 'background-color 0.2s ease',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: -100,
            right: 0,
            bottom: 0,
            background: `linear-gradient(90deg,
                var(--chakra-colors-green-200) 0%,
                var(--chakra-colors-green-200) 50%,
                var(--chakra-colors-green-400) 100%)`,
            transform: 'scaleX(0)',
            transformOrigin: 'left',
            transition: 'transform 0.2s ease-out',
          },
          '&[data-state=completed]': {
            bg: 'green.200',
            '&::before': {
              transform: 'scaleX(2)',
            },
          },
          '&[data-state=active]': {
            bg: 'gray.300',
            '&::before': {
              transform: 'scaleX(1)',
            },
          },
          '&[data-state=inactive]': {
            bg: 'gray.300',
          },
        },
      },
    },
  },
});

const tokens = defineTokens({
  fonts: {
    body: { value: fontDefault },
    heading: { value: fontDefault },
    mono: { value: fontMonospaced },
  },
  radii: {
    sm: { value: '6px' },
  },
  colors: {
    // https://www.figma.com/design/ebpCij6CDTHYedGBKuiwhF/Design-System--%3E-Library-(WIP)?node-id=1-31&m=dev
    gray: {
      '000': { value: '#FFFFFF' },
      100: { value: '#F5F5F5' },
      200: { value: '#F0F0F0' },
      300: { value: '#DFDFDF' },
      400: { value: '#CCCCCC' },
      500: { value: '#9E9E9E' },
      600: { value: '#595959' },
      700: { value: '#373737' },
      800: { value: '#121212' },
      900: { value: '#000000' },
    },
    red: {
      '000': { value: '#FFF0EE' },
      100: { value: '#FBDEDA' },
      200: { value: '#FFC6BE' },
      300: { value: '#FF6652' },
      400: { value: '#D4311C' },
      500: { value: '#A51F0F' },
      600: { value: '#891406' },
      700: { value: '#761104' },
      800: { value: '#401207' },
    },
    green: {
      '000': { value: '#DFFFEE' },
      100: { value: '#C8F9E0' },
      200: { value: '#95E8BD' },
      300: { value: '#25BD6F' },
      400: { value: '#0F8F4D' },
      500: { value: '#0E733F' },
      600: { value: '#0D5C33' },
      700: { value: '#064F29' },
    },
    yellow: {
      '000': { value: '#FFF7CD' },
      100: { value: '#FDEFC3' },
      200: { value: '#FFEEB5' },
      300: { value: '#FFE690' },
      400: { value: '#FAD148' },
      500: { value: '#D4A713' },
      600: { value: '#8B6F10' },
      700: { value: '#534000' },
    },
    blue: {
      '000': { value: '#E6F2FF' },
      100: { value: '#D7EAFE' },
      200: { value: '#BFDEFF' },
      300: { value: '#73B6FE' },
      400: { value: '#1987FF' },
      500: { value: '#0063CF' },
      600: { value: '#004B9C' },
      700: { value: '#003063' },
    },
    brand: {
      '000': { value: '#F7F7FF' },
      100: { value: '#F1F0FF' },
      200: { value: '#E0DFFF' },
      300: { value: '#ABA8FF' },
      400: { value: '#5F59FF' },
      500: { value: '#4842DF' },
      600: { value: '#2D23A7' },
      700: { value: '#07036C' },
    },
  },
});

const semanticTokens = defineSemanticTokens({
  colors: {
    border: {
      DEFAULT: {
        value: { _light: '{colors.gray.500}', _dark: '{colors.gray.400}' },
      },
      subtle: {
        value: { _light: '{colors.gray.300}', _dark: '{colors.gray.600}' },
      },
      error: {
        value: { _light: '{colors.red.300}', _dark: '{colors.red.400}' },
      },
    },
    fg: {
      muted: {
        value: { _light: '{colors.gray.600}', _dark: '{colors.gray.300}' },
      },
      error: {
        value: { _light: '{colors.red.300}', _dark: '{colors.red.400}' },
      },
    },
    icon: {
      interactive: {
        value: { _light: '{colors.gray.900}', _dark: '{colors.gray.000}' },
      },
      descriptive: {
        value: { _light: '{colors.gray.600}', _dark: '{colors.gray.300}' },
      },
      disabled: {
        value: { _light: '{colors.gray.500}', _dark: '{colors.gray.400}' },
      },
      white: {
        value: { _light: '{colors.gray.000}', _dark: '{colors.gray.900}' },
      },
      error: {
        value: { _light: '{colors.red.300}', _dark: '{colors.red.400}' },
      },
    },
    text: {
      primary: {
        value: { _light: '{colors.gray.900}', _dark: '{colors.gray.000}' },
      },
      secondary: {
        value: { _light: '{colors.gray.600}', _dark: '{colors.gray.300}' },
      },
      tertiary: {
        value: { _light: '{colors.gray.400}', _dark: '{colors.gray.500}' },
      },
      'read-only': {
        value: { _light: '{colors.gray.700}', _dark: '{colors.gray.200}' },
      },
      disabled: {
        value: { _light: '{colors.gray.500}', _dark: '{colors.gray.400}' },
      },
      white: {
        value: { _light: '{colors.gray.000}', _dark: '{colors.gray.900}' },
      },
      error: {
        value: { _light: '{colors.red.400}', _dark: '{colors.red.400}' },
      },
      discount: {
        value: { _light: '{colors.green.400}', _dark: '{colors.green.200}' },
      },
      required: {
        value: { _light: '{colors.red.500}', _dark: '{colors.red.300}' },
      },
      link: {
        enabled: {
          value: { _light: '{colors.gray.900}', _dark: '{colors.gray.000}' },
        },
        hover: {
          value: { _light: '{colors.gray.800}', _dark: '{colors.gray.100}' },
        },
        active: {
          value: { _light: '{colors.gray.700}', _dark: '{colors.gray.200}' },
        },
        focused: {
          value: { _light: '{colors.gray.900}', _dark: '{colors.gray.000}' },
        },
        disabled: {
          value: { _light: '{colors.gray.500}', _dark: '{colors.gray.400}' },
        },
      },
      'destructive-secondary': {
        enabled: {
          value: { _light: '{colors.red.500}', _dark: '{colors.red.300}' },
        },
        hover: {
          value: { _light: '{colors.red.500}', _dark: '{colors.red.300}' },
        },
        active: {
          value: { _light: '{colors.red.600}', _dark: '{colors.red.200}' },
        },
        focused: {
          value: { _light: '{colors.red.500}', _dark: '{colors.red.300}' },
        },
        selected: {
          value: { _light: '{colors.red.700}', _dark: '{colors.red.100}' },
        },
        disabled: {
          value: { _light: '{colors.gray.500}', _dark: '{colors.gray.400}' },
        },
      },
    },
    status: {
      error: {
        value: { _light: '{colors.red.400}', _dark: '{colors.red.400}' },
      },
      success: {
        value: { _light: '{colors.green.500}', _dark: '{colors.green.200}' },
      },
    },
    fill: {
      icon: {
        interactive: {
          value: { _light: '{colors.gray.900}', _dark: '{colors.gray.000}' },
        },
        descriptive: {
          value: { _light: '{colors.gray.600}', _dark: '{colors.gray.300}' },
        },
        disabled: {
          value: { _light: '{colors.gray.500}', _dark: '{colors.gray.400}' },
        },
        white: {
          value: { _light: '{colors.gray.000}', _dark: '{colors.gray.900}' },
        },
        error: {
          value: { _light: '{colors.red.400}', _dark: '{colors.red.400}' },
        },
        hover: {
          value: { _light: '{colors.gray.200}', _dark: '{colors.gray.700}' },
        },
        active: {
          value: { _light: '{colors.gray.300}', _dark: '{colors.gray.600}' },
        },
        green: {
          value: { _light: '{colors.green.500}', _dark: '{colors.green.200}' },
        },
        'destructive-secondary': {
          enabled: {
            value: { _light: '{colors.red.500}', _dark: '{colors.red.300}' },
          },
          hover: {
            value: { _light: '{colors.red.500}', _dark: '{colors.red.300}' },
          },
          active: {
            value: { _light: '{colors.red.600}', _dark: '{colors.red.200}' },
          },
          focused: {
            value: { _light: '{colors.red.500}', _dark: '{colors.red.300}' },
          },
          selected: {
            value: { _light: '{colors.red.700}', _dark: '{colors.red.100}' },
          },
          disabled: {
            value: { _light: '{colors.red.300}', _dark: '{colors.red.500}' },
          },
        },
      },
      background: {
        default: {
          value: { _light: '{colors.gray.000}', _dark: '{colors.gray.800}' },
        },
        banner: {
          value: { _light: '{colors.brand.100}', _dark: '{colors.brand.600}' },
        },
        'light-gray': {
          value: { _light: '{colors.gray.100}', _dark: '{colors.gray.900}' },
        },
        error: {
          value: { _light: '{colors.red.800}', _dark: '{colors.red.800}' },
        },
      },
      interactive: {
        enabled: {
          value: { _light: '{colors.gray.000}', _dark: '{colors.gray.900}' },
        },
        hover: {
          value: { _light: '{colors.gray.100}', _dark: '{colors.gray.800}' },
        },
        selected: {
          value: { _light: '{colors.gray.200}', _dark: '{colors.gray.700}' },
        },
        'hover-selected': {
          value: { _light: '{colors.gray.300}', _dark: '{colors.gray.600}' },
        },
      },
      input: {
        enabled: {
          value: { _light: '{colors.gray.000}', _dark: '{colors.gray.700}' },
        },
        hover: {
          value: { _light: '{colors.gray.100}', _dark: '{colors.gray.800}' },
        },
        active: {
          value: { _light: '{colors.gray.000}', _dark: '{colors.gray.900}' },
        },
        focused: {
          value: { _light: '{colors.gray.000}', _dark: '{colors.gray.900}' },
        },
        'read-only': {
          value: { _light: '{colors.gray.200}', _dark: '{colors.gray.700}' },
        },
        disabled: {
          value: { _light: '{colors.gray.000}', _dark: '{colors.gray.900}' },
        },
        error: {
          value: { _light: '{colors.gray.000}', _dark: '{colors.gray.900}' },
        },
        filled: {
          value: { _light: '{colors.gray.000}', _dark: '{colors.gray.900}' },
        },
      },
      button: {
        primary: {
          enabled: {
            value: { _light: '{colors.gray.900}', _dark: '{colors.gray.000}' },
          },
          hover: {
            value: { _light: '{colors.gray.800}', _dark: '{colors.gray.100}' },
          },
          active: {
            value: { _light: '{colors.gray.700}', _dark: '{colors.gray.200}' },
          },
          focused: {
            value: { _light: '{colors.gray.900}', _dark: '{colors.gray.000}' },
          },
          selected: {
            value: { _light: '{colors.gray.900}', _dark: '{colors.gray.000}' },
          },
          disabled: {
            value: { _light: '{colors.gray.100}', _dark: '{colors.gray.800}' },
          },
        },
        secondary: {
          enabled: {
            value: { _light: '{colors.gray.200}', _dark: '{colors.gray.700}' },
          },
          hover: {
            value: { _light: '{colors.gray.300}', _dark: '{colors.gray.600}' },
          },
          active: {
            value: { _light: '{colors.gray.400}', _dark: '{colors.gray.500}' },
          },
          focused: {
            value: { _light: '{colors.gray.200}', _dark: '{colors.gray.700}' },
          },
          selected: {
            value: { _light: '{colors.gray.700}', _dark: '{colors.gray.200}' },
          },
          disabled: {
            value: { _light: '{colors.gray.100}', _dark: '{colors.gray.800}' },
          },
        },
        tertiary: {
          enabled: {
            value: { _light: '{colors.gray.000}', _dark: '{colors.gray.900}' },
          },
          hover: {
            value: { _light: '{colors.gray.100}', _dark: '{colors.gray.800}' },
          },
          active: {
            value: { _light: '{colors.gray.200}', _dark: '{colors.gray.700}' },
          },
          focused: {
            value: { _light: '{colors.gray.000}', _dark: '{colors.gray.900}' },
          },
          selected: {
            value: { _light: '{colors.gray.000}', _dark: '{colors.gray.900}' },
          },
          disabled: {
            value: { _light: '{colors.gray.000}', _dark: '{colors.gray.900}' },
          },
        },
        pay: {
          enabled: {
            value: { _light: '{colors.brand.400}', _dark: '{colors.brand.400}' },
          },
          hover: {
            value: { _light: '{colors.brand.500}', _dark: '{colors.brand.300}' },
          },
          active: {
            value: { _light: '{colors.brand.600}', _dark: '{colors.brand.100}' },
          },
          focused: {
            value: { _light: '{colors.brand.400}', _dark: '{colors.brand.400}' },
          },
          selected: {
            value: { _light: '{colors.brand.700}', _dark: '{colors.brand.100}' },
          },
          disabled: {
            value: { _light: '{colors.gray.100}', _dark: '{colors.gray.800}' },
          },
        },
        'destructive-primary': {
          enabled: {
            value: { _light: '{colors.red.400}', _dark: '{colors.red.400}' },
          },
          hover: {
            value: { _light: '{colors.red.500}', _dark: '{colors.red.300}' },
          },
          active: {
            value: { _light: '{colors.red.600}', _dark: '{colors.red.200}' },
          },
          focused: {
            value: { _light: '{colors.red.400}', _dark: '{colors.red.400}' },
          },
          selected: {
            value: { _light: '{colors.red.700}', _dark: '{colors.red.100}' },
          },
          disabled: {
            value: { _light: '{colors.gray.100}', _dark: '{colors.gray.800}' },
          },
        },
        'destructive-secondary': {
          enabled: {
            value: { _light: '{colors.gray.000}', _dark: '{colors.gray.900}' },
          },
          hover: {
            value: { _light: '{colors.red.000}', _dark: '{colors.red.800}' },
          },
          active: {
            value: { _light: '{colors.red.000}', _dark: '{colors.red.800}' },
          },
          focused: {
            value: { _light: '{colors.gray.000}', _dark: '{colors.gray.900}' },
          },
          selected: {
            value: { _light: '{colors.gray.000}', _dark: '{colors.gray.900}' },
          },
          disabled: {
            value: { _light: '{colors.gray.000}', _dark: '{colors.gray.900}' },
          },
        },
      },
    },
    stroke: {
      border: {
        default: {
          value: { _light: '{colors.gray.500}', _dark: '{colors.gray.400}' },
        },
        disabled: {
          value: { _light: '{colors.gray.300}', _dark: '{colors.gray.600}' },
        },
        focused: {
          value: { _light: '{colors.gray.900}', _dark: '{colors.gray.000}' },
        },
        'focused-wrapper': {
          value: { _light: '{colors.gray.000}', _dark: '{colors.gray.900}' },
        },
        hover: {
          value: { _light: '{colors.gray.600}', _dark: '{colors.gray.300}' },
        },
        error: {
          value: { _light: '{colors.red.400}', _dark: '{colors.red.400}' },
        },
        menu: {
          value: { _light: '{colors.gray.300}', _dark: '{colors.gray.600}' },
        },
      },
      button: {
        tertiary: {
          enabled: {
            value: { _light: '{colors.gray.500}', _dark: '{colors.gray.400}' },
          },
          hover: {
            value: { _light: '{colors.gray.500}', _dark: '{colors.gray.400}' },
          },
          active: {
            value: { _light: '{colors.gray.600}', _dark: '{colors.gray.300}' },
          },
          focused: {
            value: { _light: '{colors.gray.900}', _dark: '{colors.gray.000}' },
          },
          selected: {
            value: { _light: '{colors.gray.900}', _dark: '{colors.gray.000}' },
          },
          disabled: {
            value: { _light: '{colors.gray.200}', _dark: '{colors.gray.700}' },
          },
        },
        'destructive-secondary': {
          enabled: {
            value: { _light: '{colors.red.500}', _dark: '{colors.red.300}' },
          },
          hover: {
            value: { _light: '{colors.red.500}', _dark: '{colors.red.300}' },
          },
          active: {
            value: { _light: '{colors.red.600}', _dark: '{colors.red.200}' },
          },
          focused: {
            value: { _light: '{colors.red.500}', _dark: '{colors.red.300}' },
          },
          selected: {
            value: { _light: '{colors.red.700}', _dark: '{colors.red.100}' },
          },
          disabled: {
            value: { _light: '{colors.gray.200}', _dark: '{colors.gray.700}' },
          },
        },
      },
      divider: {
        default: {
          value: { _light: '{colors.gray.200}', _dark: '{colors.gray.700}' },
        },
        emphasized: {
          value: { _light: '{colors.gray.400}', _dark: '{colors.gray.600}' },
        },
      },
    },
    radio: {
      border: {
        default: {
          value: { _light: '{colors.gray.300}', _dark: '{colors.gray.600}' },
        },
        selected: {
          value: { _light: '{colors.brand.400}', _dark: '{colors.brand.400}' },
        },
      },
      background: {
        default: {
          value: { _light: '{colors.gray.000}', _dark: '{colors.gray.900}' },
        },
        selected: {
          value: { _light: '{colors.brand.400}', _dark: '{colors.brand.400}' },
        },
      },
      indicator: {
        selected: {
          value: { _light: '{colors.gray.000}', _dark: '{colors.gray.000}' },
        },
      },
    },
  },
});

const breakpoints = {
  xm: '0px', // Phone Portrait
  sm: '600px', // Tablet Portrait
  md: '812px', // Tablet Landscape, Small Desktop
  lg: '1200px', // Desktop
  xl: '1440px', // Desktop Ultra-wide
};

const customConfig = defineConfig({
  theme: {
    recipes: {
      badge: badgeRecipe,
      button: buttonRecipe,
      card: cardRecipe,
      input: inputRecipe,
      link: linkRecipe,
      separator: separatorRecipe,
      spinner: spinnerRecipe,
      text: typographyRecipe,
    },
    slotRecipes: {
      accordion: accordionSlotRecipe,
      dialog: dialogSlotRecipe,
      field: fieldRecipe,
      status: statusSlotRecipe,
      stepper: stepperSlotRecipe,
      switch: switchSlotRecipe,
      tag: tagRecipe,
      toast: toastSlotRecipe,
      tooltip: tooltipSlotRecipe,
    },
    tokens,
    semanticTokens,
    breakpoints,
  },
  globalCss: {
    '*::placeholder': {
      color: 'text.tertiary',
    },
    '*::selection': {
      bg: '',
    },
  },
});

export const system = createSystem(defaultConfig, customConfig);
